import { authClient, AuthenticatedMeResponse } from '@/api/client/auth.client';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { triggerContractGateOnFirstLogin } from '@/shared/lib/auth/first-login';
import { isProfileComplete } from '@/shared/lib/auth/profile-completion';
import { captureError } from '@/shared/lib/errors/capture';
import { ensurePostHogInit } from '@/shared/lib/analytics/posthog-init';
import { clearSentryUser, identifySentryUser } from '@/shared/lib/sentry-user';
import { useUploadStore } from '@/shared/stores/upload-store';
import { useUploadHistoryStore } from '@/shared/stores/upload-history-store';
import {
  isTwoFactorRequired,
  type LoginOutcome,
  type TwoFactorCompleteInput,
} from '@/shared/lib/auth/two-factor';
import { describeTwoFactorError } from '@/shared/lib/auth/two-factor-errors';
import { isAxiosError } from 'axios';
import type {
  AuthResponse,
  FirebaseLoginData,
  LoginCredentials,
  RegisterData,
  User,
} from '@/types';
import posthog from 'posthog-js';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

const IS_DEV = process.env.NODE_ENV === 'development';

const IMPERSONATION_PROPS = [
  'is_impersonation',
  'impersonated_user_id',
  'impersonated_user_email',
  'impersonated_user_handle',
  'impersonation_started_at',
  'impersonator_email',
] as const;

/** Safe PostHog calls — no-ops in dev mode (PostHog is never initialized there) */
function phIdentify(distinctId: string, properties?: Record<string, unknown>) {
  if (IS_DEV) return;
  // The rehydrate → checkAuth → fetchUser chain can resolve before the React
  // provider effect runs init on slow-hydration devices; posthog-js drops
  // pre-init calls silently (FRONT-273). Init here is idempotent + synchronous.
  ensurePostHogInit();
  posthog.identify(distinctId, properties);
}

/**
 * Person properties for `identify` — one builder for every auth path (login,
 * register, firebase, boot-time fetchUser). `user_id` + `has_discord_access`
 * bridge the FE person to the BE-emitted `taboo_ai.*` events (which carry only
 * the numeric `user_id`) and enable Discord-cohort segmentation — without them
 * the two identity spaces can't be joined in PostHog.
 */
function identifyProps(user: User, subscribed: boolean): Record<string, unknown> {
  return {
    email: user.email,
    name:
      (user.display_name ?? [user.first_name, user.last_name].filter(Boolean).join(' ')) ||
      user.email,
    subscribed,
    user_id: user.id,
    has_discord_access: !!user.has_discord_access,
  };
}
function phCapture(
  event: string,
  properties?: Record<string, unknown>,
  options?: { send_instantly?: boolean }
) {
  if (IS_DEV) return;
  ensurePostHogInit();
  posthog.capture(event, properties, options);
}
function phUnregister(keys: readonly string[]) {
  if (IS_DEV) return;
  for (const key of keys) posthog.unregister(key);
}
function phReset() {
  if (IS_DEV) return;
  posthog.reset();
}

interface AuthState {
  user: User | null;
  isSubscribed: boolean;
  isProfileComplete: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInitialized: boolean;
  error: string | null;
  _hasHydrated: boolean;

  login: (credentials: LoginCredentials) => Promise<LoginOutcome>;
  register: (data: RegisterData) => Promise<void>;
  firebaseLogin: (
    data: FirebaseLoginData
  ) => Promise<LoginOutcome & { requires_username?: boolean }>;
  /**
   * Second half of a login that hit the 2FA challenge. Resolves to the same
   * session body `/api/login` would have given; rejects with the axios error
   * (branch on `describeTwoFactorError`).
   */
  completeTwoFactor: (input: TwoFactorCompleteInput) => Promise<AuthResponse>;
  logout: (opts?: { redirectTo?: string }) => Promise<void>;
  fetchUser: () => Promise<void>;
  /**
   * Merge the owner-only `location` from `/me` into the current user. Login,
   * register, 2FA, Whop and checkout responses build the user before the
   * request is authenticated, so the BE leaves `location` out of them.
   * Resolves `false` only when a later retry could still help (request failed,
   * or the signed-in user changed mid-flight).
   */
  refreshUserLocation: () => Promise<boolean>;
  updateUser: (user: Partial<User>) => void;
  setSubscribed: (subscribed: boolean) => void;
  setUserFromPostCheckout: (user: User, subscribed: boolean) => void;
  clearError: () => void;
  checkAuth: () => Promise<void>;
}

let pendingAuthCheck: Promise<void> | null = null;
let pendingLocationRefresh: { userId: number; promise: Promise<boolean> } | null = null;

type AuthFailureClass = '4xx' | '5xx' | 'no-status';

function authFailureClass(status: number | undefined): AuthFailureClass {
  if (status === undefined) return 'no-status';
  return status < 500 ? '4xx' : '5xx';
}

/**
 * 4xx on a sign-in entry = wrong password / validation / throttle — the user's
 * problem, not ours: kept visible for spike detection, out of alerts. Every
 * failure of one entry shares a stack, so without the status-class fingerprint
 * Sentry grouped real 5xx into the noise-tagged 4xx issue (JAVASCRIPT-NEXTJS-7Q).
 */
function captureAuthFailure(error: unknown, action: 'login' | 'register' | 'firebase-login') {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  const failureClass = authFailureClass(status);
  captureError(error, {
    component: 'AuthStore',
    action,
    fingerprint: ['auth', action, failureClass],
    ...(failureClass === '4xx' ? { severity: 'warning', tags: { signal: 'noise' } } : {}),
    ...(status !== undefined ? { extra: { http_status: status } } : {}),
  });
}

/**
 * One post-login block for every entry that yields a session (password,
 * register, Firebase). Callers narrow the 2FA challenge away first — a
 * challenge never reaches here, so `isAuthenticated` can't flip without a user.
 */
function applySession(set: (patch: Partial<AuthState>) => void, response: AuthResponse) {
  const subscribed = response.subscribed ?? false;
  set({
    user: response.user,
    isSubscribed: subscribed,
    isProfileComplete: isProfileComplete(response.user),
    isAuthenticated: true,
    isInitialized: true,
    isLoading: false,
  });
  phIdentify(response.user.email, identifyProps(response.user, subscribed));
  identifySentryUser(response.user.id, subscribed);
  triggerContractGateOnFirstLogin({
    last_login: response.last_login,
    user: response.user,
  });
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isSubscribed: false,
      isProfileComplete: false,
      isLoading: false,
      isAuthenticated: false,
      isInitialized: false,
      error: null,
      _hasHydrated: false,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });
        try {
          // Client-side: /api/login proxy sets HttpOnly cookie
          const response = await authClient.login(credentials);
          if (isTwoFactorRequired(response)) {
            set({ isLoading: false });
            return { twoFactorRequired: true, challengeToken: response.challenge_token };
          }
          applySession(set, response);
          return { twoFactorRequired: false };
        } catch (error: unknown) {
          captureAuthFailure(error, 'login');
          const message = error instanceof Error ? error.message : 'Login failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null });
        try {
          // Client-side: /api/register proxy sets HttpOnly cookie
          applySession(set, await authClient.register(data));
        } catch (error: unknown) {
          captureAuthFailure(error, 'register');
          const message = error instanceof Error ? error.message : 'Registration failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      firebaseLogin: async (data: FirebaseLoginData) => {
        set({ isLoading: true, error: null });
        try {
          // Client-side: /api/auth/firebase-login proxy sets HttpOnly cookie
          const response = await authClient.firebaseLogin(data);
          if (isTwoFactorRequired(response)) {
            set({ isLoading: false });
            return { twoFactorRequired: true, challengeToken: response.challenge_token };
          }
          applySession(set, response);
          return {
            twoFactorRequired: false,
            ...(response.requires_username !== undefined
              ? { requires_username: response.requires_username }
              : {}),
          };
        } catch (error: unknown) {
          captureAuthFailure(error, 'firebase-login');
          const message = error instanceof Error ? error.message : 'Social login failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      completeTwoFactor: async ({ challengeToken, code, recoveryCode, rememberMe }) => {
        set({ isLoading: true, error: null });
        try {
          // Client-side: /api/2fa/verify proxy sets the HttpOnly cookie
          const response = await authClient.twoFactorVerify({
            challenge_token: challengeToken,
            ...(code !== undefined ? { code } : {}),
            ...(recoveryCode !== undefined ? { recovery_code: recoveryCode } : {}),
            ...(rememberMe !== undefined ? { remember_me: rememberMe } : {}),
          });
          applySession(set, response);
          return response;
        } catch (error: unknown) {
          // Wrong / replayed / throttled codes are the user's problem — keep
          // them visible for spike detection, out of alerts. A `client_bug`
          // slug (pending token on the wrong route, empty submit) is ours and
          // stays at error level.
          const info = describeTwoFactorError(error);
          const noisy = info.kind !== 'client_bug' && info.kind !== 'network';
          captureError(error, {
            component: 'AuthStore',
            action: 'two-factor-verify',
            ...(noisy ? { severity: 'warning', tags: { signal: 'noise' } } : {}),
            extra: {
              error_code: info.errorCode,
              ...(info.httpStatus !== undefined ? { http_status: info.httpStatus } : {}),
            },
          });
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async (opts) => {
        set({ isLoading: true });
        try {
          // Client-side: /api/logout proxy clears HttpOnly cookie
          await authClient.logout();
        } catch (error) {
          captureError(error, { component: 'AuthStore', action: 'logout', severity: 'warning' });
          // Ignore logout errors - clear local state anyway
        } finally {
          // Fire stopped event BEFORE unregister so it carries the impersonation
          // super-props as audit context, then strip them so the logout event is clean.
          const impersonatingHandle = sessionStorage.getItem('impersonating');
          if (impersonatingHandle) {
            phCapture(
              AnalyticsEvent.ADMIN_IMPERSONATION_STOPPED,
              { via: 'logout', impersonated_user_handle: impersonatingHandle },
              { send_instantly: true }
            );
          }
          phUnregister(IMPERSONATION_PROPS);
          sessionStorage.removeItem('impersonating');
          phCapture(AnalyticsEvent.AUTH_LOGOUT_COMPLETED, {}, { send_instantly: true });
          phReset();
          clearSentryUser();
          // Wipe upload state so the next user / signed-out visitor on this
          // browser doesn't see the previous user's filenames, bunny IDs, or
          // partial-progress URLs (the GlobalUploadIndicator and studio table
          // both render directly from these persisted entries).
          try {
            useUploadStore.persist.clearStorage();
            useUploadHistoryStore.persist.clearStorage();
            for (let i = localStorage.length - 1; i >= 0; i--) {
              const key = localStorage.key(i);
              if (key?.startsWith('tus::')) localStorage.removeItem(key);
            }
          } catch (error) {
            captureError(error, {
              component: 'AuthStore',
              action: 'logout-clear-uploads',
              severity: 'warning',
            });
          }
          set({
            user: null,
            isSubscribed: false,
            isProfileComplete: false,
            isAuthenticated: false,
            isInitialized: false,
            isLoading: false,
          });
          window.location.href = opts?.redirectTo ?? '/sign-in';
        }
      },

      fetchUser: async () => {
        set({ isLoading: true });
        try {
          const response: AuthenticatedMeResponse = await authClient.me();
          if (response.authenticated && response.user) {
            const subscribed = response.subscribed ?? (response.user as User)?.subscribed ?? false;
            const user = response.user as User;
            set({
              user,
              isSubscribed: subscribed,
              isProfileComplete: isProfileComplete(user),
              isAuthenticated: true,
              isLoading: false,
            });
            phIdentify(user.email, identifyProps(user, subscribed));
            identifySentryUser(user.id, subscribed);
          } else {
            set({
              user: null,
              isSubscribed: false,
              isProfileComplete: false,
              isAuthenticated: false,
              isLoading: false,
            });
            clearSentryUser();
          }
        } catch (error) {
          // 401 = guest/stale cookie hitting /me — part of the auth contract,
          // not a failure. It was the top quota-burner in both Sentry (5.9k
          // events) and PostHog client_error (1.8k/3d); never capture it.
          const status = isAxiosError(error) ? error.response?.status : undefined;
          // No response = the request was dropped client-side (iOS WebKit
          // aborts in-flight XHRs on backgrounding — Sentry 98/J2, 100% WebKit).
          const noResponse = isAxiosError(error) && !error.response;
          if (status !== 401) {
            captureError(error, {
              component: 'AuthStore',
              action: 'fetch-user',
              severity: 'warning',
              ...(noResponse ? { tags: { signal: 'noise' } } : {}),
            });
          }
          // Only the server saying "not you" ends the session. Clearing on a
          // dropped request or a 5xx wiped the persisted user and showed paying
          // members guest/paywall UI until the next successful check.
          if (status !== 401 && status !== 403) {
            set({ isLoading: false });
            return;
          }
          set({
            user: null,
            isSubscribed: false,
            isProfileComplete: false,
            isAuthenticated: false,
            isLoading: false,
          });
          clearSentryUser();
        }
      },

      refreshUserLocation: () => {
        const userId = get().user?.id;
        if (userId === undefined) return Promise.resolve(false);
        if (pendingLocationRefresh?.userId === userId) return pendingLocationRefresh.promise;
        const promise = (async (): Promise<boolean> => {
          try {
            const response = await authClient.me();
            const current = get().user;
            if (!current || current.id !== userId || response.user?.id !== userId) return false;
            // A share / opt-out written while /me was in flight is fresher than
            // this answer — the refresh only fills a missing value.
            if (current.location != null) return true;
            const location = (response.user as User).location;
            if (!location) {
              // Not noise: /me is the only source of sharing state, and the
              // location prompt stays hidden while it is unknown — without this
              // report a BE regression silently stops the prompt for everyone.
              captureError(new Error('GET /me returned no user.location'), {
                component: 'AuthStore',
                action: 'refresh-user-location-missing',
                severity: 'warning',
              });
              return true;
            }
            set({ user: { ...current, location } });
            return true;
          } catch (error: unknown) {
            // Transient; the host retries on a later navigation. Not actionable
            // on its own — /me failures surface through fetchUser too.
            captureError(error, {
              component: 'AuthStore',
              action: 'refresh-user-location',
              severity: 'warning',
              tags: { signal: 'noise' },
            });
            return false;
          } finally {
            if (pendingLocationRefresh?.userId === userId) pendingLocationRefresh = null;
          }
        })();
        pendingLocationRefresh = { userId, promise };
        return promise;
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          const updatedUser = { ...currentUser, ...userData };
          set({
            user: updatedUser,
            isProfileComplete: isProfileComplete(updatedUser),
          });
        }
      },

      setSubscribed: (subscribed: boolean) => {
        set({ isSubscribed: subscribed });
      },

      setUserFromPostCheckout: (user: User, subscribed: boolean) => {
        set({
          user,
          isSubscribed: subscribed,
          isProfileComplete: isProfileComplete(user),
          isAuthenticated: true,
          isInitialized: true,
          isLoading: false,
        });
        phIdentify(user.email, {
          email: user.email,
          name:
            (user.display_name ?? [user.first_name, user.last_name].filter(Boolean).join(' ')) ||
            user.email,
          subscribed,
        });
        identifySentryUser(user.id, subscribed);
      },

      clearError: () => set({ error: null }),

      checkAuth: async () => {
        if (pendingAuthCheck) return pendingAuthCheck;
        pendingAuthCheck = (async () => {
          try {
            await get().fetchUser();
          } catch (error) {
            captureError(error, {
              component: 'AuthStore',
              action: 'check-auth',
              severity: 'warning',
            });
            set({
              user: null,
              isSubscribed: false,
              isProfileComplete: false,
              isAuthenticated: false,
            });
          } finally {
            set({ isInitialized: true });
            pendingAuthCheck = null;
          }
        })();
        return pendingAuthCheck;
      },
    }),
    {
      name: 'tabootv-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isSubscribed: state.isSubscribed,
        isProfileComplete: state.isProfileComplete,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => {
        return (state) => {
          if (!state) return;
          useAuthStore.setState({ _hasHydrated: true });
          if (state.user) {
            // Optimistic for UI rendering only — NOT for redirect decisions
            // Force isInitialized: false to override stale value from old localStorage
            useAuthStore.setState({ isAuthenticated: true, isInitialized: false });
          } else {
            // Wait for server confirmation before considering initialized
            useAuthStore.setState({ isInitialized: false, isAuthenticated: false });
          }
          // Always verify auth with server — handles cookie-exists-but-localStorage-cleared edge case
          // pendingAuthCheck deduplication prevents double calls
          useAuthStore.getState().checkAuth();
        };
      },
    }
  )
);

// Safety net: ensure _hasHydrated is set even if onRehydrateStorage doesn't fire
if (typeof window !== 'undefined') {
  useAuthStore.persist.onFinishHydration(() => {
    console.log('[auth-store] onFinishHydration called');
    const state = useAuthStore.getState();
    if (!state._hasHydrated) {
      console.warn('[auth-store] _hasHydrated was still false after hydration, forcing it');
      useAuthStore.setState({ _hasHydrated: true });
    }
  });
}
